import getConfig from 'next/config';
import { fetchKbbRating, fetchOwnersFromBYC } from '../fetchers';
import { allMakeValue, phoneNumberLabel } from '../constants/index';
import { enableKBBSeoMakes } from '.';

const isNumber = (value) => Number.isNaN(Number(value));

const mapDealerObject = (item, i) => ({
    distanceFromSearch: item.Distance,
    // the VendorDealerIds in byc dealer list maybe be duplicated but the dealer card component is identify by id,
    // so maybe we add the makeId to id to work well with dealer card component
    id: ((item.PartnerFranchiseId !== null && item.PartnerFranchiseId.length > 0) && !isNumber(item.PartnerFranchiseId))
        ? parseFloat(item.PartnerFranchiseId) + item.MakeId + i
        : `isNull${i}`,
    name: item.FranchiseName,
    franchiseName: item.FranchiseName,
    ownerTitleCTA: item.FranchiseAndMake,
    location: {
        address: {
            address1: item.FranchiseAddress1,
            city: item.FranchiseCity,
            state: item.FranchiseState,
            zip: item.FranchiseZip,
        },
        map: {
            href: null,
        },
    },
    phone: {
        privateNumber: false,
        label: phoneNumberLabel,
        value: item.FranchisePhone ? item.FranchisePhone.replace(/-/g, '') : '',
        visible: true,
    },
    primaryPhotoUrl: item.PrimaryPhoto,
    storefrontWebURL: item.StorefrontWebURL,
    rating: item.rating,
    isShowCase: item.HasShowcaseTreatment,
    hasDealerDetail: item.LeadSource === 'LSC' ? item.PartnerFranchiseId != null : item.OwnerProduct !== null,
    website: {
        href: `?manufacturername=${item.MakeName}&franchisekey=${(item.FranchiseId * 1000) + item.MakeId}`,
    },
    makeId: item.MakeId,
    makeName: item.MakeName,
    dealerId: item.PartnerFranchiseId,
    isPayingDealer: item.IsServiceDealer,
});

export default async (dealerQuery, query) => {
    let bycOwners = {
        owners: null,
        totalResultCount: null,
    };
    const { publicRuntimeConfig } = getConfig();
    dealerQuery.makeCode = (query.make === allMakeValue) ? '' : query.make;

    const bycResponses = await fetchOwnersFromBYC(dealerQuery);
    if (!bycResponses) {
        return {
            owners: [],
            totalResultCount: 0,
        };
    }

    // Batch fetch KBB ratings for multiple dealers
    const fetchBatchKbbRatings = async (dealers) => {
        const validDealers = dealers.filter((dealer) => dealer.VendorDealerId); // Filter dealers with VendorDealerId

        // Batch fetch all ratings at once
        const kbbRatings = await Promise.all(
            validDealers.map((dealer) => fetchKbbRating(dealer.VendorDealerId))
        );

        // Create a map of VendorDealerId to rating
        const ratingsMap = new Map(
            kbbRatings.map((rating, index) => [
                validDealers[index].VendorDealerId,
                rating,
            ]).filter(([, rating]) => rating !== null)
        );

        return ratingsMap;
    };

    // Process all dealers with batched KBB ratings
    const processAllDealers = async () => {
        const kbbRatingsMap = await fetchBatchKbbRatings(bycResponses); // Fetch all KBB ratings at once

        return Promise.all(bycResponses.map((dealer, index) => {
            if (dealer.VendorDealerId && kbbRatingsMap.has(dealer.VendorDealerId)) {
                const kbbRating = kbbRatingsMap.get(dealer.VendorDealerId);
                dealer = {
                    ...dealer,
                    rating: (kbbRating.count === 0 || kbbRating.kbbDealerRating < 1) ? null : {
                        count: kbbRating.count,
                        value: kbbRating.kbbDealerRating,
                    },
                };
            }

            const enrichedDealer = mapDealerObject(dealer, index);

            if (enrichedDealer.IsServiceDealer) {
                enrichedDealer.isPayingDealer = true;
            }

            enrichedDealer.website.href = `${publicRuntimeConfig.fdpqAppUrl}${enrichedDealer.website.href}`;
            if (query.psid) {
                enrichedDealer.website.href += `&psid=${query.psid}`;
            }

            return enrichedDealer;
        }));
    };

    const dealers = await processAllDealers();

    bycOwners = {
        owners: dealers.sort((a, b) => (b.isShowCase - a.isShowCase) || (a.distanceFromSearch - b.distanceFromSearch)),
        totalResultCount: dealers.length,
        restrictedSeoMakes: enableKBBSeoMakes(dealers),
    };

    return bycOwners;
};
